/**
 * Forgot password, and set a new one — Better Auth's reset flow, U65.
 *
 * The mailed link is Better Auth's `/api/auth/reset-password/{token}`, which redirects here
 * with `?token=` when the token is live and `?error=INVALID_TOKEN` when it is not. The request
 * form answers the same way for every address, because Better Auth does.
 */
import { useState, type FormEvent, type ReactNode } from "react";
import { useNavigate } from "@tanstack/react-router";

import { Button, Input, Surface, SurfaceBody, SurfaceFooter } from "@packagepage/ui";

import { authClient, authErrorMessage } from "../api/auth.js";

import "@packagepage/ui/tokens.css";
import "@packagepage/ui/surface.css";
import "@packagepage/ui/button.css";
import "@packagepage/ui/input.css";
import "./auth.css";

/** `instance.ts`'s floor, the same constant `SignIn.tsx` states before it enforces it. */
const MIN_PASSWORD_LENGTH = 12;

function Shell({ label, children }: { label: string; children: ReactNode }): ReactNode {
  return (
    <div className="pp-auth" data-screen-label={label}>
      <div className="pp-auth__panel">
        <div className="pp-auth__wordmark">
          <span className="pp-auth__mark" aria-hidden="true">
            p
          </span>
          <span>packagepage</span>
        </div>
        <Surface className="pp-auth__card" sectioned>
          {children}
          <SurfaceFooter>
            <p className="pp-auth__switch">
              Remembered it? <a href="/signin">Sign in</a>
            </p>
          </SurfaceFooter>
        </Surface>
      </div>
    </div>
  );
}

export function ForgotPassword(): ReactNode {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    const result = await authClient.requestPasswordReset({
      email,
      redirectTo: "/reset-password",
    });
    setBusy(false);
    if (result.error) {
      setError(authErrorMessage(result.error));
      return;
    }
    setSent(true);
  }

  return (
    <Shell label="Forgot password">
      <SurfaceBody>
        <div className="pp-auth__card-stack">
          <div>
            <div className="pp-auth__title">Reset your password</div>
            <p className="pp-auth__lede">
              {sent
                ? `If ${email} has an account, a reset link is on its way. It works for an hour.`
                : "Enter the address you sign in with and we will email you a link."}
            </p>
          </div>
          {error === null ? null : (
            <Surface tone="error" emphasis="filled">
              {error}
            </Surface>
          )}
          {sent ? null : (
            <form className="pp-auth__form" onSubmit={(event) => void submit(event)}>
              <Input
                label="Email"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                autoComplete="email"
                required
              />
              <Button type="submit" variant="primary" loading={busy}>
                {busy ? "Sending…" : "Email me a link"}
              </Button>
            </form>
          )}
        </div>
      </SurfaceBody>
    </Shell>
  );
}

export function ResetPassword(): ReactNode {
  const navigate = useNavigate();
  const params = new URLSearchParams(window.location.search);
  const token = params.get("token");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(
    token === null
      ? "This reset link has expired or was already used. Ask for a new one below."
      : null,
  );
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (token === null) return;
    setError(null);
    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`A password here is at least ${String(MIN_PASSWORD_LENGTH)} characters.`);
      return;
    }
    setBusy(true);
    const result = await authClient.resetPassword({ newPassword: password, token });
    setBusy(false);
    if (result.error) {
      setError(authErrorMessage(result.error));
      return;
    }
    await navigate({ to: "/signin" });
  }

  return (
    <Shell label="Reset password">
      <SurfaceBody>
        <div className="pp-auth__card-stack">
          <div className="pp-auth__title">Choose a new password</div>
          {error === null ? null : (
            <Surface tone="error" emphasis="filled">
              {error}
            </Surface>
          )}
          {token === null ? (
            <p className="pp-auth__lede">
              <a href="/forgot-password">Email me a new link</a>
            </p>
          ) : (
            <form className="pp-auth__form" onSubmit={(event) => void submit(event)}>
              <div className="pp-auth__field">
                <Input
                  label="New password"
                  type="password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  autoComplete="new-password"
                  required
                />
                <span className="pp-auth__slug">
                  At least {MIN_PASSWORD_LENGTH} characters.
                </span>
              </div>
              <Button type="submit" variant="primary" loading={busy}>
                {busy ? "Saving…" : "Save password"}
              </Button>
            </form>
          )}
        </div>
      </SurfaceBody>
    </Shell>
  );
}
